/**
 * Web-client constants. Anything that appears in more than one file lives here
 * (coding conventions → "no magic strings").
 */

/** Product name, used in titles, nav and legal copy. */
export const PRODUCT_NAME = 'Gridiron Tracker';

/**
 * FERPA acknowledgement — AUTHORITATIVE COPY. Reproduced verbatim from
 * `quality/strategy.md` ("Signup FERPA acknowledgement gate") and
 * `product.program-type`. The signup smoke test asserts this string character
 * for character and PR merge is blocked on any deviation; do not reflow,
 * re-punctuate or "fix" the em dashes.
 */
export const FERPA_ACKNOWLEDGEMENT_TEXT =
  'I understand that Gridiron Tracker is designed for football program data — player names, cohort (grade, year, or squad), jersey numbers, positions, participation, and play statistics — which are not FERPA-protected education records. I will not enter FERPA-protected education records into this platform, such as date of birth, contact information, guardian information, medical notes, photos, disciplinary records, or academic records.';

/** Where the browser keeps client state. Session tokens are per-tab by default. */
export const STORAGE_KEYS = {
  session: 'gridiron.session.v1',
  theme: 'gridiron.theme.v1',
  lastTeamSlug: 'gridiron.last-team-slug.v1',
  /** Width of the docs table-of-contents pane; a per-device UI preference. */
  docsTocWidth: 'gridiron.docs-toc-width.v1',
  signupDraft: 'gridiron.signup-draft.v1',
  /**
   * Active-event context (FR-EVENTS-08). localStorage, so the choice survives a
   * page reload on a sideline tablet; cleared on sign-out by the provider.
   */
  activeEvent: 'gridiron.active-event.v1',
  /**
   * The tenant the active event was selected under (staff: the session's
   * `tid`; Fanatic: the granted team in `activeTenant`). The provider drops a
   * persisted event whose scope is not the current one, so a Fanatic switching
   * teams — or a stale grant — never sees another team's game in the header.
   */
  activeEventScope: 'gridiron.active-event-scope.v1',
  /**
   * The Fanatic portal's selected team (`{ tenantId, slug, name }`). A Fanatic
   * token carries no `tid`, so this — mirrored into `?team=` — is what resolves
   * the `X-Tenant-ID` header for every team-scoped read (AC-FANATIC-004).
   */
  fanaticTeam: 'gridiron.fanatic-team.v1',
  /**
   * Which custom stat columns the Play Archive table shows (FR-ARCHIVE-01).
   * localStorage: a coach's column choice is a per-device preference, and the
   * URL already carries the filters.
   */
  archiveCustomColumns: 'gridiron.archive-custom-columns.v1',
  /**
   * Trial-expiry prompts (FR-BILLING-03). sessionStorage (per tab): dismissing
   * the 3-day banner or acknowledging "continue on Free" silences the prompt
   * for the current session only — it comes back on the next sign-in while the
   * trial is still expiring.
   */
  trialBannerDismissed: 'gridiron.trial-banner-dismissed.v1',
  trialModalAcknowledged: 'gridiron.trial-modal-acknowledged.v1',
  /**
   * Contact Support draft (FR-HELP-06). sessionStorage: a half-written request
   * survives a reload or an accidental overlay close, but is not left on a
   * shared sideline tablet after the tab is gone. The attachment is never
   * stored — a `File` cannot be serialised and re-picking it is trivial.
   */
  supportDraft: 'gridiron.support-draft.v1',
  /** Docked sidebar width in px, and whether it is collapsed to an icon rail. */
  sidebarWidth: 'gridiron.sidebar-width.v1',
  sidebarCollapsed: 'gridiron.sidebar-collapsed.v1',
} as const;

/** API paths, relative to `VITE_API_BASE_URL`. Gateway owns `/{service}/v{N}`. */
export const API_PATHS = {
  signupOptions: '/signup/v1',
  signup: '/signup/v1',
  slugAvailability: '/signup/v1/slug-availability',
  teamLookup: '/signup/v1/team-lookup',
  /**
   * auth-service: public sign-in options for the FAN funnel. Anonymous — the
   * fan sign-in page must know which social buttons to render before anyone
   * has signed in, and a fan has no tenant context to supply.
   */
  fanSignInOptions: '/auth/v1/fan/sign-in-options',
  /** auth-service: self-service Fanatic signup. Public. */
  fanaticSignup: '/auth/v1/signup/fanatic',
  token: '/auth/v1/token',
  tokenRefresh: '/auth/v1/token/refresh',
  tokenRevoke: '/auth/v1/token/revoke',
  /** auth-service: anti-enumeration reset request — 202 for every outcome. */
  passwordResetRequest: '/auth/v1/password/reset/request',
  /** auth-service: single-use token + new password — 204 on success. */
  passwordResetConfirm: '/auth/v1/password/reset/confirm',
  /** auth-service: "where am I signed in" list + revocation (FR-AUTH-04). */
  sessions: '/auth/v1/sessions',
  mfaEnroll: '/auth/v1/mfa/enroll',
  mfaVerify: '/auth/v1/mfa/verify',
  oauthAuthorize: '/auth/v1/oauth',
  tenant: '/tenants/v1',
  users: '/users/v1',
  /** roster-service: player CRUD. */
  players: '/players/v1',
  /** roster-service: tenant cohort catalog. */
  cohortValues: '/cohort-values/v1',
  /** events-service: event CRUD, lifecycle (`/close`, `/reopen`) and participation. */
  events: '/events/v1',
  /** plays-service: play log, versions, conflict resolution. */
  plays: '/plays/v1',
  /** playbook-service: the tenant's play library (O/D/ST). */
  playbook: '/playbook/v1',
  /** stat-schema-service: the tenant's custom stat field registry (FR-STATS-01..04). */
  statSchema: '/stat-schema/v1',
  /** stats-service: read-only aggregates (highlight, summary, tracker, season). */
  stats: '/stats/v1',
  /** billing-service: PUBLIC plan catalog (FR-BILLING-01). */
  billingPlans: '/billing/v1/plans',
  /** billing-service: `{tenant_id}` sub-path — plan, entitlements, trial, usage. */
  billingSubscriptions: '/billing/v1/subscriptions',
  /** billing-service: invoice history (filter/sort/paginate + dunning block). */
  billingInvoices: '/billing/v1/invoices',
  /** billing-service: the ONLY card-capture door (FR-BILLING-02). */
  billingCheckoutSessions: '/billing/v1/checkout-sessions',
  /** billing-service: provider-hosted portal for payment-method changes. */
  billingPortal: '/billing/v1/portal',
  /** plays-service: the tenant's Success Measure rules (FR-PLAY-07). */
  successMeasureConfig: '/plays/v1/success-measure-config',
  /** video-service: provider capability catalog (every authenticated persona). */
  videoProviders: '/video/v1/providers',
  /** video-service: tenant provider selection (Team Admin). */
  videoSettings: '/video/v1/settings',
  /** video-service: per-event embed configuration (Team Admin). */
  videoEmbeds: '/video/v1/embeds',
  /** video-service: embed delivery, Fanatic-gated by provider capability. */
  videoEmbed: '/video/v1/embed',
  /** hudl-service: exportable fields + the fixed Hudl playlist columns. */
  hudlExportFields: '/hudl/v1/export/fields',
  /** hudl-service: saved custom export layouts (CRUD). */
  hudlExportTemplates: '/hudl/v1/export/templates',
  /** hudl-service: synchronous file download (xlsx / csv). */
  hudlExportFile: '/hudl/v1/export/file',
  /** Site-wide table export (hudl-service): `POST` the page's rows, get the file. */
  tableExport: '/exports/v1/table',
  exportTemplates: '/exports/v1/templates',
  /** hudl-service: upload a file and get a preview back. */
  hudlImportPreview: '/hudl/v1/import/preview',
  /** hudl-service: `/{upload_id}/reconcile` and `/{upload_id}/commit`. */
  hudlImport: '/hudl/v1/import',
  /** hudl-service: recent imports. */
  hudlImports: '/hudl/v1/imports',
  /** help-service: published FAQ library, persona-filtered and searchable. */
  faqs: '/help/v1/faqs',
  /** help-service: tooltip text resolved tenant > platform > shipped default. */
  tooltips: '/help/v1/tooltips',
  /** support-service: Contact Support submissions + the caller's own history. */
  supportRequests: '/support/v1/requests',
  /**
   * support-service: is a support recipient configured for this team?
   * Contact Support disables its submit button when this reports `false`.
   */
  supportRecipientStatus: '/support/v1/recipient-status',
  /** fanatic-service: the signed-in Fanatic's own grants ("teams I follow"). */
  fanaticGrants: '/fanatic/v1/grants',
  /** fanatic-service: public team directory (`/teams`) and one team (`/teams/{slug}`). */
  fanaticTeams: '/fanatic/v1/teams',
  /** fanatic-service: shared Fanatic realm metadata (IdP realm, invite TTL). */
  fanaticRealm: '/fanatic/v1/realm',
  /** fanatic-service: invite preview (`/{token}`) and accept (`/{token}/accept`). */
  fanaticInvites: '/fanatic/v1/invites',
  /** auth-service: Fanatic self-service email change (fanatic-only at the gateway). */
  account: '/auth/v1/account',
  /** auth-service: OAuth identities linked to the signed-in account. */
  accountIdentities: '/auth/v1/account/identities',
  /** Per-account MFA opt-in / opt-out (`GET` / `PATCH`). */
  accountMfa: '/auth/v1/account/mfa',
} as const;

/** Window events used to decouple the API client from React state. */
export const APP_EVENTS = {
  /** Dispatched when any authenticated call comes back 401. */
  unauthorized: 'gridiron:unauthorized',
  /**
   * Dispatched when the active-event context switcher changes the session's
   * event (FR-EVENTS-08). `detail` is `{ eventId: string | null }`. The live
   * update client subscribes to this to re-route its WebSocket channel without
   * taking a React dependency on the switcher.
   */
  activeEventChanged: 'gridiron:active-event-changed',
  /**
   * Dispatched when any authenticated call comes back 402
   * `TIER_LIMIT_EXCEEDED` (FR-BILLING-05). `detail` is the `ApiError`, so the
   * app-wide `<TierLimitBoundary>` can render the upgrade CTA with the
   * limit/usage numbers the service reported — from anywhere in the app,
   * without every caller having to handle 402 itself.
   */
  tierLimitExceeded: 'gridiron:tier-limit-exceeded',
} as const;

/**
 * Tier-limit rejection (FR-BILLING-05). Mirror of the contract in
 * `packages/shared/src/tier-gate.ts`: HTTP 402 + code `TIER_LIMIT_EXCEEDED`,
 * with `details: { limit_key, limit, current, requested, current_plan,
 * minimum_plan, upgrade_path }`.
 */
export const TIER_LIMIT_STATUS = 402;
export const TIER_LIMIT_ERROR_CODE = 'TIER_LIMIT_EXCEEDED';
/** Registry key for the tier-limit CTA tooltip — canonical, never renamed. */
export const TIER_LIMIT_TOOLTIP_ID = 'tier.limit.cta';

/** Client-side routes. */
export const ROUTES = {
  landing: '/',
  /** Dedicated plan-comparison page (Zone 1). */
  pricing: '/pricing',
  /** Public legal pages, linked from the marketing footer (Zone 1). */
  terms: '/terms',
  privacy: '/privacy',
  login: '/login',
  signup: '/signup',
  /** Bare `/fans` (a trimmed URL) redirects to `fanSignup`. */
  fans: '/fans',
  /**
   * The FAN funnel's own front door (F1/F6).
   *
   * Fans are a different audience from coaching staff: they have no team code,
   * never need MFA, and arrive from a link mid-game on a phone. Sending them
   * through the staff `/login` — which opens by demanding a team code — is the
   * fastest way to lose them, so the two doors are separate routes rather than
   * a mode toggle on one page.
   */
  fanLogin: '/fans/login',
  fanSignup: '/fans/join',
  /** Self-service reset request (public). */
  forgotPassword: '/forgot-password',
  /**
   * Reset-link landing page (public). The PATH IS A WIRE CONTRACT with the
   * auth service's reset email: `AUTH_PASSWORD_RESET_URL_BASE` points here and
   * the email appends `?token=…&email=…`.
   */
  resetPassword: '/reset-password',
  oauthCallback: '/auth/callback',
  postAuth: '/post-auth',
  admin: '/admin',
  /**
   * Documentation site (FR-HELP-05). Persona-scoped but NOT tenant-scoped: the
   * bundled Markdown is identical for every team, so the route sits beside
   * `/app/{tenantSlug}` rather than inside it, and a deep link such as
   * `/app/docs/play-entry#conflict-resolution` is shareable across portals.
   */
  docs: '/app/docs',
  fanaticPortal: '/app/fanatic-portal',
  /**
   * Public-facing entry points for the Fanatic funnel. Both are deep links a
   * team can paste into an email, so they live at the site root rather than
   * under `/app/…`; the guard bounces an anonymous visitor to
   * `/login?next=…` and the flow resumes after sign-in.
   */
  fanaticBrowse: '/fanatic/browse',
  fanaticInviteAccept: '/fanatic/accept',
} as const;

/**
 * Views inside the Fanatic portal, relative to {@link ROUTES.fanaticPortal}.
 * The selected team travels in `?team={slug}` so a refresh or a shared link
 * restores the same team context (AC-FANATIC-004).
 */
export const FANATIC_VIEWS = {
  home: '',
  gameSummary: 'game-summary',
  liveVideo: 'live-video',
  browse: 'browse',
  account: 'account',
} as const;

export type FanaticViewId = (typeof FANATIC_VIEWS)[keyof typeof FANATIC_VIEWS];

/** Query parameter carrying the Fanatic's selected team slug. */
export const FANATIC_TEAM_PARAM = 'team';
/** Query parameter carrying an invite token on the accept landing page. */
export const FANATIC_INVITE_PARAM = 'invite_token';

/** Tenant-scoped view slugs under `/app/{tenant_slug}/…`. */
export const APP_VIEWS = {
  home: '',
  coachesHighlight: 'coaches-highlight',
  gameSummary: 'game-summary',
  playEntry: 'play-entry',
  /** Field-by-field help for Play Entry — desktop shows no inline tooltips. */
  playEntryHelp: 'play-entry/help',
  playArchive: 'play-archive',
  statTracker: 'stat-tracker',
  /** Dedicated full-screen Live Video page with persistent stat strip (Zone 3). */
  liveVideo: 'live-video',
  playbook: 'playbook',
  /** Event Manager list; the detail page is `events/{event_id}`. */
  events: 'events',
  roster: 'roster',
  teamUsers: 'team/users',
  /** Team Admin → Fanatic management: access mode, followers, invites (AC-FANATIC-009..011). */
  teamFanatics: 'team/fanatics',
  /** Team Admin → custom stat fields (FR-STATS-01..04). */
  teamStatFields: 'team/stat-fields',
  /** Team Admin → billing portal (FR-BILLING-01/02/05). */
  billing: 'billing',
  /**
   * Team Admin → Team Settings: general, video provider, support email and the
   * Success Measure override panel (FR-TENANT-05, FR-VIDEO-03, FR-HELP-07,
   * FR-PLAY-07).
   */
  teamSettings: 'team/settings',
  /** Team Admin → Integrations: Hudl CSV export, mapping presets (FR-HUDL-01..05). */
  teamIntegrations: 'team/integrations',
  /**
   * Team Admin → Identity Providers: SAML 2.0 federation, SCIM 2.0 directory
   * sync and the per-persona provider policy (FR-AUTH-01/02/06). Elite only —
   * the page renders the upgrade CTA when the auth service answers 402.
   */
  teamIdentityProviders: 'team/identity-providers',
  settings: 'settings',
} as const;

/**
 * FR-FERPA-ACK-02 — the reminder shown on the custom stat field form.
 *
 * VERBATIM AND MERGE-BLOCKING. `custom-stat-banner.smoke.test.tsx` asserts the
 * rendered text equals this string exactly. Do not reword, re-punctuate or
 * re-wrap it; changing it requires a spec change to FR-FERPA-ACK-02.
 */
export const FERPA_CUSTOM_STAT_REMINDER_TEXT =
  'Reminder: Custom stat fields are for football-related statistics only. ' +
  'Do not enter FERPA-protected education records (date of birth, contact information, ' +
  'guardian information, medical notes, photos, disciplinary or academic records) ' +
  'as custom stat values.';

/**
 * Audited free-form reasons (close, reopen, post-close edit) must clear the
 * audit spec's floor. Mirror of `REASON_MIN_LENGTH` / `REASON_MAX_LENGTH` in
 * `services/events/src/constants.ts`; the service remains the authority.
 */
export const EVENT_REASON_MIN_LENGTH = 10;
export const EVENT_REASON_MAX_LENGTH = 1000;
/** Mirror of `PARTICIPATION_NOTES_MAX_LENGTH` in services/events. */
export const PARTICIPATION_NOTES_MAX_LENGTH = 500;

/** Rows per page offered by every list screen. */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 10;

/**
 * Slug + password policies. Mirrors of the server-side rules in
 * `services/tenant/src/constants.ts` and `domain/owner-password.ts` so the form
 * can validate inline; the server remains the authority.
 */
export const TENANT_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const TENANT_SLUG_MIN_LENGTH = 3;
export const TENANT_SLUG_MAX_LENGTH = 50;
export const TEAM_NAME_MIN_LENGTH = 2;
export const TEAM_NAME_MAX_LENGTH = 100;
export const OWNER_PASSWORD_MIN_LENGTH = 12;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Trial terms shown on the marketing page and in the signup wizard. */
export const TRIAL = {
  days: 30,
  planDisplayName: 'Varsity',
  downgradePlanDisplayName: 'Free',
} as const;

/**
 * Fallback consent-document versions for display only. The live values come
 * from `GET /signup/v1` (`consent.dpa_version`) and the server records whatever
 * version it holds — the client never sends one.
 */
export const CONSENT_DOCUMENT_VERSIONS = {
  dpa: '1.0',
  tos: '1.0',
} as const;

// ---------------------------------------------------------------------------
// Starred plays (team-wide favorites) — user-facing copy, one place to translate
// ---------------------------------------------------------------------------

export const STAR_COPY = {
  star: 'Star',
  unstar: 'Unstar',
  starred: 'Starred',
  play: 'play',
  thisPlay: 'this play',
  starTitle: 'Star this play for later review (the whole staff sees it)',
  unstarTitle: 'Starred for review — click to unstar',
  starredTitle: 'Starred for review',
  cardTitle: 'Starred plays',
  cardEmpty: 'No starred plays yet',
  cardEmptyHint: 'Star a play in Play Entry or the Play Archive and it shows up here.',
  /** Empty state per board tab — the card lists only that tab's unit. */
  cardEmptyOffense: 'No starred offense plays yet',
  cardEmptyDefense: 'No starred defense plays yet',
  /** Call line (phase-aware concept + formation) labels, used as hover titles. */
  conceptLabel: 'Concept',
  formationLabel: 'Formation',
  defConceptLabel: 'Def. concept',
  oppFormationLabel: 'Opp. formation',
  stConceptLabel: 'ST concept',
  stReturnConceptLabel: 'Return concept',
  stFormationLabel: 'ST formation',
  /** Shown when the concept was never logged on the play. */
  notLogged: '—',
  failed: 'The star could not be saved. Please try again.',
  /** Play Entry Actions card before this operator has logged a play. */
  logFirst: 'Log a play first, then star it for review.',
  logFirstShort: 'Log a play first',
  columnTitle: 'Starred',
} as const;

// ---------------------------------------------------------------------------
// Saved board layouts (Coaches Highlight + Play Entry) — user-facing copy
// ---------------------------------------------------------------------------

/**
 * The single home for the Layout dropdown / save / rename / delete copy on
 * Coaches Highlight and Play Entry (several named layouts per screen).
 */
export const LAYOUT_COPY = {
  layout: 'Layout',
  /** Trigger label prefix for the switcher button. */
  switcherLabel: 'Saved layout',
  switcherTitle: 'Switch between your saved layouts and the team layouts',
  myLayouts: 'My layouts',
  teamLayouts: 'Team layouts',
  standardBoard: 'Standard board',
  saveLayout: 'Save to My layouts',
  saveTitle: 'Save this board to My layouts — only you see it',
  /** Under the name field of the Save dialog: where a personal save lands. */
  saveUserScopeNote:
    'Saved under My layouts. Only you see it — your teammates’ boards do not change.',
  saveAsTeamDefault: 'Save as team default',
  saveAsTeamDefaultTitle: 'Publish this board as a team layout every member can load',
  /** Under the name field of the team dialog: who receives a team save. */
  saveTeamScopeNote:
    'Saved under Team layouts. Every member of the team can load it; only a Team Admin can rename or delete it.',
  layoutName: 'Layout name',
  layoutNameHint: 'Unique on this screen, e.g. "Red zone" or "2-minute".',
  rename: 'Rename',
  renameLayout: 'Rename layout',
  delete: 'Delete',
  deleteLayout: 'Delete layout',
  confirmDelete: 'Delete',
  confirmDeleteConsequence: (name: string): string =>
    `"${name}" is removed for good. If it is the layout on screen, your next layout loads instead (else a team layout, else the standard board).`,
  confirmDeleteTeamConsequence: (name: string): string =>
    `"${name}" is removed for every member of the team. If it is on screen, your next layout loads instead (else another team layout, else the standard board).`,
  save: 'Save',
  cancel: 'Cancel',
  /** Hint under the name field when Save will overwrite an existing layout. */
  overwriteHint: (name: string): string => `Saves over "${name}".`,
  nameRequired: 'Give the layout a name.',
  nameTooLong: 'Layout names are at most 80 characters.',
  nameTaken: (name: string): string =>
    `A layout named "${name}" already exists on this screen. Pick another name.`,
  cannotManageTeam: 'Only a Team Admin can rename or delete a team layout.',
  cannotManageDefault: 'The standard board cannot be renamed or deleted.',
  activeLabel: (name: string): string => `Layout: ${name}`,
  /** Ownership badges: menu rows, the trigger and the Customize strip. */
  teamBadge: 'Team',
  userBadge: 'Mine',
  noUserLayouts: 'None yet — Customize the board and Save to add one.',
  noTeamLayouts: 'None yet — a Team Admin can publish one with Save as team default.',
  /** Customize-strip badge for what is loaded. */
  ownershipUser: 'My layout',
  ownershipTeam: 'Team layout',
  ownershipStandard: 'Standard',
  ownershipTitleUser: 'Personal layout — only you see it',
  ownershipTitleTeam: 'Team layout — shared with every member',
  ownershipTitleStandard: 'The built-in board — Save keeps a copy under My layouts',
} as const;

/** Longest layout name the users service accepts (`board_layouts.layout_name`). */
export const LAYOUT_NAME_MAX_LENGTH = 80;

// ---------------------------------------------------------------------------
// Site-wide table export (POST /exports/v1/table) — limits + user-facing copy
// ---------------------------------------------------------------------------

/**
 * Mirror of the hudl-service limits (`services/hudl/src/constants.ts`). Set
 * well above a real season (a full season is a few thousand plays × ~60
 * columns); beyond them the server answers 413 `EXPORT_TOO_LARGE`.
 */
export const EXPORT_MAX_CELLS = 100_000;
export const EXPORT_MAX_COLUMNS = 100;
export const EXPORT_MAX_PLAYS = 10_000;
export const EXPORT_LIMITS = {
  cells: EXPORT_MAX_CELLS,
  columns: EXPORT_MAX_COLUMNS,
  rows: EXPORT_MAX_PLAYS,
} as const;

/** Friendly limit messages keyed by the 413's `details.limit_key`. */
export const EXPORT_LIMIT_MESSAGES: Readonly<Record<keyof typeof EXPORT_LIMITS, string>> = {
  cells: `Exports are limited to ${EXPORT_MAX_CELLS.toLocaleString('en-US')} cells (rows × columns). Filter to fewer plays or rows, or pick fewer columns, then export again.`,
  columns: `Exports are limited to ${EXPORT_MAX_COLUMNS} columns. Untick the columns you don't need, then export again.`,
  rows: `Exports are limited to ${EXPORT_MAX_PLAYS.toLocaleString('en-US')} rows. Filter to fewer plays or rows (one game or one season at a time), then export again.`,
};

export const EXPORT_COPY = {
  button: 'Export',
  buttonTitle: 'Download this table as an Excel or CSV file',
  dialogTitle: (page: string): string => `Export ${page}`,
  format: 'Format',
  hudl: 'Hudl compatible',
  hudlSummary:
    'The 19-column spreadsheet Hudl imports, one row per play — only the plays this page is showing, in its order.',
  custom: 'Custom',
  customHelp:
    'Starts from the columns this page is showing. Untick a column to leave it out, rename a heading, or move it up and down. Your team’s custom stats are included.',
  templateHelp:
    'Templates are shared with your whole staff and work on every page that exports: a template picks the columns this page has.',
  columnsIncluded: (included: number, total: number): string => `${included} of ${total} columns`,
  rowsIncluded: (rows: number): string =>
    `${rows.toLocaleString('en-US')} ${rows === 1 ? 'row' : 'rows'}`,
  fileType: 'File type',
  excel: 'Excel (.xlsx)',
  csv: 'CSV (.csv)',
  include: (label: string): string => `Include ${label}`,
  heading: (label: string): string => `Column heading for ${label}`,
  moveUp: (label: string): string => `Move ${label} up`,
  moveDown: (label: string): string => `Move ${label} down`,
  saveAsTemplate: 'Save as template…',
  saveTemplate: 'Save template',
  saveChanges: 'Save changes',
  templateName: 'Template name',
  templateNamePlaceholder: 'e.g. My Standard Layout',
  deleteTemplate: 'Delete',
  deleteTitle: 'Delete this template?',
  deleteConsequence: (name: string): string =>
    `“${name}” will no longer be offered as an export format on any page, for anyone on your staff. Files already downloaded are not affected.`,
  deleteConfirm: 'Delete template',
  duplicateName: (name: string): string =>
    `Your team already has a template called “${name}”. Pick another name.`,
  nameRequired: 'Give the template a name.',
  noMatchingColumns: 'None of this template’s columns are on this page.',
  download: 'Download',
  preparing: 'Preparing file…',
  downloaded: (file: string): string => `Downloaded ${file}`,
  pickColumn: 'Include at least one column.',
  noRows: 'There are no rows to export with the current filters.',
  failed: 'The file could not be created. Please try again.',
  saved: (name: string): string => `Saved “${name}”.`,
  updated: (name: string): string => `Updated “${name}”.`,
  deleted: (name: string): string => `Deleted “${name}”.`,
  tooLargeHint: 'This is over the export limit — filter to fewer rows or untick some columns.',
} as const;
